import { useCallback, useEffect, useMemo } from "react";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { Modal } from "components/common/Modal";
import { FolderSuggestionHeader } from "./FolderSuggestionHeader";
import { FolderSuggestionStatus } from "./FolderSuggestionStatus";
import { FolderSuggestionSteps } from "./FolderSuggestionSteps";
import { useFloorPlanFolderSuggestion } from "./useFloorPlanFolderSuggestion";
import { useImportFloorPlanFolders } from "./useImportFloorPlanFolders";
import { pickFolderSuggestionView } from "./utils";
import type { ImportFloorPlanFoldersVariables } from "./useImportFloorPlanFolders";
import type { FolderSuggestionModalProps } from "./types";

export const FolderSuggestionModal = ({
  isOpen,
  projectId,
  planRecords,
  folderRecords,
  onClose,
  onFoldersImported,
}: FolderSuggestionModalProps) => {
  const {
    suggestion,
    suggestionUpdatedAt,
    isSuggesting,
    suggestError,
    requestSuggestion,
    clearSuggestion,
  } = useFloorPlanFolderSuggestion({ projectId });

  const { isImporting, importFolders } = useImportFloorPlanFolders({
    projectId,
    onFoldersImported,
  });

  const startSuggestion = useCallback(async () => {
    trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_FOLDER_SUGGESTION_REQUESTED);
    await requestSuggestion();
  }, [requestSuggestion]);

  // Every opening asks again, so a suggestion never holds plans deleted or renamed since
  const closeModal = useCallback(async () => {
    onClose();
    await clearSuggestion();
  }, [onClose, clearSuggestion]);

  const applyFolders = useCallback(
    async (variables: ImportFloorPlanFoldersVariables) => {
      try {
        await importFolders(variables);
        await closeModal();
      } catch {
        // Already reported by onError.
      }
    },
    [importFolders, closeModal],
  );

  const view = useMemo(
    () => pickFolderSuggestionView({ suggestion, isSuggesting, suggestError }),
    [suggestion, isSuggesting, suggestError],
  );

  const activePlanCount = useMemo(
    () => planRecords.filter((plan) => plan.archivedAt === null).length,
    [planRecords],
  );

  const shouldStartSuggestion =
    isOpen && suggestion === null && !isSuggesting && suggestError === null;

  useEffect(() => {
    if (shouldStartSuggestion) startSuggestion();
  }, [shouldStartSuggestion, startSuggestion]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      size="xl"
      className="flex max-h-[90vh] max-w-7xl flex-col"
      disableBackdropClose={isImporting}
      disableEscapeClose={isImporting}
    >
      {view.view === "steps" ? (
        <FolderSuggestionSteps
          // A new suggestion starts the steps over.
          key={suggestionUpdatedAt}
          suggestion={view.suggestion}
          suggestError={suggestError}
          planRecords={planRecords}
          folderRecords={folderRecords}
          isImporting={isImporting}
          onSuggestAgain={startSuggestion}
          onClose={closeModal}
          onApply={applyFolders}
        />
      ) : (
        <>
          <FolderSuggestionHeader
            subtitle={null}
            isBusy={false}
            onSuggestAgain={
              view.variant === "empty" ? startSuggestion : undefined
            }
            onClose={closeModal}
          />
          <FolderSuggestionStatus
            variant={view.variant}
            planCount={activePlanCount}
            onRetry={startSuggestion}
            onClose={closeModal}
          />
        </>
      )}
    </Modal>
  );
};
